import React, { useEffect, useState } from 'react';
import { ClockIcon } from 'lucide-react';
import type { Tool } from '../../types/tools';
import { SectionHeading } from '../ui/SectionHeading';
import { ToolOutputPreview } from './ToolOutputPreview';
import { loadVimeoApi } from '../../hooks/useVimeoProgress';
import { trackEvent } from '../../utils/analytics';

/** "See [Tool] in Action" — lazy Vimeo embed when a real ID exists, otherwise a "Demo coming soon" preview. */
export function ToolDemo({
  tool
}: {
  tool: Tool;
}) {
  return <section id="demo" aria-labelledby="demo-heading" className="border-b border-hairline-soft bg-surface" data-id="element-1104" data-mp="1muf1u8c4uy7">
      <div className="mx-auto w-full max-w-[1100px] px-4 py-20 sm:px-6 lg:px-8 lg:py-24" data-id="element-1105" data-mp="6k4d1y9siexn">
        <SectionHeading eyebrow="Demo" headingId="demo-heading" title={`See ${tool.name} in Action`} align="center" data-id="element-1106" data-mp="bl0f06akzr0f" />
        <div className="mt-10" data-id="element-1107" data-mp="1mz5otfxh6v3o">
          {tool.videoId ? <VimeoEmbed tool={tool} videoId={tool.videoId} data-id="element-1108" data-mp="1mxum7jfrnte5" /> : <ComingSoon tool={tool} data-id="element-1109" data-mp="1j8f0qan2z86n" />}
        </div>
        {tool.videoId && tool.transcript ? <details className="mt-6 rounded-xl border border-hairline bg-panel px-5 py-4" data-id="element-1110" data-mp="1f73gylqw1h6d">
            <summary className="cursor-pointer text-sm font-semibold text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lime" data-id="element-1111" data-mp="1a80cp3iqfezg">
              Read the transcript
            </summary>
            <p className="mt-3 whitespace-pre-line text-sm leading-relaxed text-text-secondary" data-id="element-1112" data-mp="109kob8b3ajtn">{tool.transcript}</p>
          </details> : null}
      </div>
    </section>;
}
function VimeoEmbed({
  tool,
  videoId
}: {
  tool: Tool;
  videoId: string;
}) {
  const [iframe, setIframe] = useState<HTMLIFrameElement | null>(null);
  useEffect(() => {
    if (!iframe) return;
    let detach: (() => void) | undefined;
    let played = false;
    loadVimeoApi().then(Vimeo => {
      const player = new Vimeo.Player(iframe);
      player.on('play', () => {
        if (played) return;
        played = true;
        trackEvent('tool_demo_play', {
          tool: tool.slug
        });
      });
      detach = () => player.off('play');
    }).catch(() => {
      /* Analytics only. */
    });
    return () => detach?.();
  }, [iframe, tool.slug]);
  return <div className="relative w-full overflow-hidden rounded-2xl border border-hairline bg-black" style={{
    aspectRatio: '16 / 9'
  }} data-id="element-1113" data-mp="3q3eyulv4ckes">
      <iframe ref={setIframe} src={`https://player.vimeo.com/video/${encodeURIComponent(videoId)}?badge=0&autopause=0&dnt=1`} title={`${tool.name} demo video — DiligenceGPT™`} loading="lazy" allow="fullscreen; picture-in-picture; clipboard-write; encrypted-media" referrerPolicy="strict-origin-when-cross-origin" allowFullScreen className="absolute inset-0 h-full w-full border-0" data-id="element-1114" data-mp="lndpcfaj970z" />
    </div>;
}
function ComingSoon({
  tool
}: {
  tool: Tool;
}) {
  return <div className="relative w-full overflow-hidden rounded-2xl border border-hairline bg-elevated" style={{
    aspectRatio: '16 / 9'
  }} data-id="element-1115" data-mp="19dq6mgro4g48">
      <div className="pointer-events-none absolute inset-x-[8%] top-[8%] select-none opacity-40" aria-hidden="true" data-id="element-1116" data-mp="2p40lk4q28r3j">
        <ToolOutputPreview tool={tool} variant="compact" data-id="element-1117" data-mp="33tg9lcy7iifj" />
      </div>
      <div className="absolute inset-0 bg-elevated/70" aria-hidden="true" data-id="element-1118" data-mp="33urddcrnjq6n" />
      <div className="absolute inset-0 flex flex-col items-center justify-end gap-2 p-6 text-center sm:justify-center" data-id="element-1119" data-mp="2yubyggrq2amk">
        <span className="inline-flex items-center gap-1.5 rounded-full border border-hairline bg-panel px-3 py-1.5 text-xs font-semibold text-text-primary shadow-sm" data-id="element-1120" data-mp="2b62ku24nwt6i">
          <ClockIcon className="h-3.5 w-3.5 text-lime" aria-hidden="true" data-id="element-1121" data-mp="2l370rk7wzsev" />
          Demo coming soon
        </span>
        <p className="max-w-sm text-sm text-text-secondary" data-id="element-1122" data-mp="2g213d93b7gz0">
          A walkthrough of {tool.name} is in production. The thumbnail behind this note is illustrative.
        </p>
      </div>
    </div>;
}